import { LitElement, html, css, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { api } from "../api";
import type { PullRequestInfo, MfeVersion, EntryVersion, Environment } from "../types";
import { getPreviewOrigin } from "../preview-utils";
import { getSnapshotOverrideTokens } from "../snapshot-utils";

@customElement("pr-detail-dialog")
export class PrDetailDialog extends LitElement {
  static styles = css`
    :host {
      display: block;
      font-family: Geist, sans-serif;
    }

    .table-wrap {
      max-height: min(60vh, 640px);
      overflow: auto;
    }

    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 13px;
    }

    th {
      text-align: left;
      padding: var(--spacing-8, 8px);
      border-bottom: 2px solid var(--colors-border-border-gray-300, #e0e3e8);
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: var(--colors-foreground-fg-gray-700, #5a6072);
    }

    td {
      padding: var(--spacing-8, 8px);
      border-bottom: 1px solid var(--colors-border-border-gray-300, #e0e3e8);
    }

    td.mono {
      font-family: monospace;
      font-size: 12px;
    }

    .version-select {
      font-family: monospace;
      font-size: 12px;
      min-width: 10ch;
      max-width: 16ch;
    }

    .version-select::part(combobox) {
      font-family: monospace;
      font-size: 12px;
      min-height: 28px;
      padding: 2px 6px;
    }

    .version-loading {
      font-size: 11px;
      color: var(--colors-foreground-fg-gray-700, #5a6072);
      font-style: italic;
    }

    .patching-row td {
      opacity: 0.5;
    }

    .changed-row td {
      background: var(--colors-background-bg-brand-50, #eef2ff);
    }

    .auto-save-hint {
      font-size: 11px;
      color: var(--colors-foreground-fg-gray-700, #5a6072);
      font-style: italic;
    }

    .modal-footer {
      display: flex;
      gap: var(--spacing-8, 8px);
      justify-content: flex-end;
      align-items: center;
      width: 100%;
    }

    .header-meta {
      display: flex;
      align-items: center;
      gap: var(--spacing-8, 8px);
      margin-bottom: var(--spacing-12, 12px);
      font-size: 12px;
      color: var(--colors-foreground-fg-gray-700, #5a6072);
    }

    .empty {
      padding: var(--spacing-24, 24px);
      text-align: center;
      color: var(--colors-foreground-fg-gray-700, #5a6072);
      font-size: 13px;
    }

    .override-url-row {
      display: flex;
      align-items: center;
      gap: var(--spacing-8, 8px);
      margin-bottom: var(--spacing-12, 12px);
    }

    .copied-indicator {
      font-size: 12px;
      color: var(--colors-foreground-fg-success-500, #2e7d32);
    }

    .category-header td {
      font-weight: 600;
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: var(--colors-foreground-fg-gray-700, #5a6072);
      background: var(--colors-background-bg-secondary, #f9fafb);
    }
  `;

  @property({ attribute: false }) pr!: PullRequestInfo;

  private get _isRelease(): boolean {
    return this.pr.target === "prod";
  }

  @state() private _versions: Record<string, MfeVersion> = {};
  @state() private _shell: Record<string, EntryVersion> = {};
  @state() private _services: Record<string, EntryVersion> = {};
  @state() private _loading = true;
  @state() private _error = "";
  @state() private _availableVersions: Map<string, string[]> = new Map();
  @state() private _loadingVersions: Set<string> = new Set();
  @state() private _patchingMfe: string | null = null;
  @state() private _copied = false;
  @state() private _showPrerelease = false;

  async connectedCallback() {
    super.connectedCallback();
    await this._loadVersions();
  }

  private async _loadVersions() {
    this._loading = true;
    this._error = "";
    try {
      const data = await api.getPromotionVersions(this.pr.number);
      this._versions = data.mfes;
      this._shell = data.shell ?? {};
      this._services = data.services ?? {};
    } catch (e: unknown) {
      this._error = e instanceof Error ? e.message : `Failed to load ${this._isRelease ? "release" : "promotion"} versions`;
    } finally {
      this._loading = false;
    }
  }

  private _close = () => {
    this.dispatchEvent(new CustomEvent("close"));
  };

  private async _fetchVersions(mfeName: string) {
    if (this._availableVersions.has(mfeName) || this._loadingVersions.has(mfeName)) return;

    this._loadingVersions = new Set([...this._loadingVersions, mfeName]);
    try {
      const { versions } = await api.getMfeVersions(mfeName, this._showPrerelease);
      this._availableVersions = new Map([...this._availableVersions, [mfeName, versions]]);
    } catch {
      this._availableVersions = new Map([...this._availableVersions, [mfeName, []]]);
    } finally {
      const next = new Set(this._loadingVersions);
      next.delete(mfeName);
      this._loadingVersions = next;
    }
  }

  private _togglePrerelease() {
    this._showPrerelease = !this._showPrerelease;
    this._availableVersions = new Map();
  }

  private async _handleVersionChange(mfeName: string, newVersion: string) {
    const current = this._versions[mfeName];
    if (!current || current.resolved === newVersion) return;

    this._patchingMfe = mfeName;
    this._error = "";
    try {
      await api.patchPromotion(this.pr.number, { mfe: mfeName, newVersion });
      this._versions = {
        ...this._versions,
        [mfeName]: { major: current.major, resolved: newVersion },
      };
      this.dispatchEvent(new CustomEvent("patched"));
    } catch (e: unknown) {
      this._error = e instanceof Error ? e.message : "Failed to patch version";
    } finally {
      this._patchingMfe = null;
    }
  }

  private _getOverrideUrl(): string {
    const env: Environment = this._isRelease ? "stage" : (this.pr.source || "dev");
    const origin = getPreviewOrigin(env);
    const tokens = getSnapshotOverrideTokens(this._versions, null);
    if (tokens.length === 0) return origin;
    return `${origin}?overrides=${tokens.join(",")}`;
  }

  private async _copyOverrideUrl() {
    const url = this._getOverrideUrl();
    await navigator.clipboard.writeText(url);
    this._copied = true;
    setTimeout(() => { this._copied = false; }, 2000);
  }

  render() {
    return html`
      <bee-modal
        open
        in-root
        title="${this._isRelease ? "Release" : "Promotion"} #${this.pr.number}"
        @bee-request-close=${this._close}
      >
        <div class="header-meta">
          <bee-tag color=${this._isRelease ? "warning" : "brand"} small>${this._isRelease ? "Release" : "Promotion"}</bee-tag>
          <span>${this.pr.author}</span>
          ${!this._isRelease && this.pr.source && this.pr.target ? html`
            <span>|</span>
            <span>${this.pr.source} -> ${this.pr.target}</span>
          ` : nothing}
          <span>|</span>
          <bee-link href=${this.pr.url} target="_blank" rel="noopener noreferrer">View PR on GitHub</bee-link>
        </div>

        ${this._loading
          ? html`<div class="empty"><bee-spinner size="medium" type="blue"></bee-spinner></div>`
          : this._renderContent()}

        ${this._error ? html`<bee-alert variant="error" open><span slot="message">${this._error}</span></bee-alert>` : nothing}

        <div slot="footer" class="modal-footer">
          <span class="auto-save-hint">Changes save automatically</span>
          <bee-button label="View PR on GitHub" bee-type="secondary-grey" icon="external-link" @click=${() => window.open(this.pr.url, "_blank", "noopener,noreferrer")}></bee-button>
          <bee-button label="Done" bee-type="primary" @click=${this._close}></bee-button>
        </div>
      </bee-modal>
    `;
  }

  private _renderContent() {
    const sortedMfes = Object.entries(this._versions).sort(([a], [b]) => a.localeCompare(b));
    const sortedShell = Object.entries(this._shell).sort(([a], [b]) => a.localeCompare(b));
    const sortedServices = Object.entries(this._services).sort(([a], [b]) => a.localeCompare(b));
    const totalEntries = sortedMfes.length + sortedShell.length + sortedServices.length;

    if (totalEntries === 0) {
      return html`<div class="empty">No versions found on PR branch.</div>`;
    }

    const hasMultipleSections = [sortedShell.length, sortedMfes.length, sortedServices.length].filter(Boolean).length > 1;

    return html`
      <div class="override-url-row">
        <bee-button
          label="Copy Override URL"
          bee-type="secondary"
          size="small"
          @click=${() => this._copyOverrideUrl()}
        ></bee-button>
        ${this._copied ? html`<span class="copied-indicator">Copied</span>` : nothing}
        <bee-toggle size="small" .checked=${this._showPrerelease} @bee-change=${() => this._togglePrerelease()}>Show pre-release</bee-toggle>
      </div>
      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Version</th>
            </tr>
          </thead>
          <tbody>
            ${sortedShell.length > 0 ? html`
              ${hasMultipleSections ? html`<tr class="category-header"><td colspan="2">Shell</td></tr>` : nothing}
              ${sortedShell.map(([name, entry]) => html`
                <tr><td>${name}</td><td class="mono">${entry.version}</td></tr>
              `)}
            ` : nothing}
            ${sortedMfes.length > 0 ? html`
              ${hasMultipleSections ? html`<tr class="category-header"><td colspan="2">MFEs</td></tr>` : nothing}
              ${sortedMfes.map(([name, version]) => this._renderVersionRow(name, version))}
            ` : nothing}
            ${sortedServices.length > 0 ? html`
              ${hasMultipleSections ? html`<tr class="category-header"><td colspan="2">Services</td></tr>` : nothing}
              ${sortedServices.map(([name, entry]) => html`
                <tr><td>${name}</td><td class="mono">${entry.version}</td></tr>
              `)}
            ` : nothing}
          </tbody>
        </table>
      </div>
    `;
  }

  private _isChangedInPr(name: string): boolean {
    return this.pr.changes.some((c) => c.name === name);
  }

  private _renderVersionRow(name: string, version: MfeVersion) {
    const isPatching = this._patchingMfe === name;
    const isChanged = this._isChangedInPr(name);
    const isLoading = this._loadingVersions.has(name);
    const versions = this._availableVersions.get(name);

    const allVersions = versions && versions.length > 0
      ? (versions.includes(version.resolved) ? versions : [version.resolved, ...versions])
      : [version.resolved];

    const rowClass = isPatching ? "patching-row" : isChanged ? "changed-row" : "";

    return html`
      <tr class=${rowClass}>
        <td>${name}</td>
        <td class="mono">
          <bee-select
            class="version-select"
            size="small"
            hoist
            .value=${version.resolved}
            ?disabled=${isPatching}
            @mouseenter=${() => this._fetchVersions(name)}
            @bee-show=${() => this._fetchVersions(name)}
            @bee-change=${(e: CustomEvent) => this._handleVersionChange(name, (e.target as HTMLSelectElement).value)}
          >
            ${allVersions.map(
              (v) => {
                return html`<bee-option value=${v}>${v}</bee-option>`;
              },
            )}
          </bee-select>
          ${isLoading && !versions ? html`<span class="version-loading">loading...</span>` : nothing}
        </td>
      </tr>
    `;
  }
}
